<template>
    <div class="page">
        <h1>Summary</h1>

        <section>
            <div class="heading-container">
                <h2>Parameters</h2>
                <div class="button-container">
                    <router-link to="/tank-parameters" class="button">Change</router-link>
                </div>
            </div>

            <template v-if="usingSuggestedParameters">
                <p>These parameters have been automatically generated from your chosen shrimp for best compatibility.</p>
            </template>
            <template v-else>
                <p>These are the parameters you provided. You can automatically find the best parameters for your shrimp by changing to <strong>Automatic</strong> mode.</p>
            </template>


            <div class="tank-parameters-container with-bottom-margin">
                <div>
                    <span class="label">GH</span>
                    <span class="value">{{ getParamTextFromRange(tankParameters.gh) }}</span>
                </div>
                <div>
                    <span class="label">KH</span>
                    <span class="value">{{ getParamTextFromRange(tankParameters.kh) }}</span>
                </div>
                <div>
                    <span class="label">pH</span>
                    <span class="value">{{ getParamTextFromRange(tankParameters.ph) }}</span>
                </div>
            </div>
        </section>

        <section>
            <div class="heading-container">
                <h2>Shrimp</h2>
                <div class="button-container">
                    <router-link to="/shrimp-select" class="button">Change</router-link>
                </div>
            </div>

            <div v-if="totalShrimpWithWarningsCount > 0" class="warning-intro">
                <p><strong>Warning:</strong> There may be parameter issues with <strong>{{ totalShrimpWithWarningsCount }}</strong> shrimp</p>
            </div>

            <ul class="shrimp-summary-list">
                <li v-for="shrimp in selectedShrimp" :key="shrimp.id">
                    <div class="shrimp-info">
                        <div class="name-and-image">
                            <div class="names">
                                <span class="name">{{ shrimp.name }}</span>
                                <span class="species">{{ shrimp.speciesName }}</span>
                            </div>
                            <div class="image-container">
                                <img :src="getImagePathForShrimp(shrimp)" @error="imageFailure" :alt="shrimp.name" />
                            </div>
                        </div>

                        <div class="parameters">
                            <div :data-status="getShrimpParameterStatus(shrimp, 'gh')">
                                <span class="label">GH</span>
                                <span class="value">{{ getParamTextFromRange(shrimp.ranges.gh) }}</span>
                            </div>
                            <div :data-status="getShrimpParameterStatus(shrimp, 'kh')">
                                <span class="label">KH</span>
                                <span class="value">{{ getParamTextFromRange(shrimp.ranges.kh) }}</span>
                            </div>
                            <div :data-status="getShrimpParameterStatus(shrimp, 'ph')">
                                <span class="label">pH</span>
                                <span class="value">{{ getParamTextFromRange(shrimp.ranges.ph) }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="getWarningsForShrimp(shrimp).length > 0" class="warnings">
                        <ul>
                            <li v-for="warningText in getWarningsForShrimp(shrimp)" :key="warningText">
                                {{ warningText }}
                            </li>
                        </ul>
                    </div>
                </li>
            </ul>
        </section>

        <section class="share">
            <h2>Share</h2>
            <div>
                <p>You can let others view your tank setup by sharing the link below.</p>
                <a :href="shareUrl" target="_blank" class="share-link">{{ shareUrl }}</a>
                <div class="button-container">
                    <button v-if="showNativeShare" @click="openSharePrompt" class="button">Share</button>
                    <a :href="twitterShareUrl" target="_blank" rel="noopener" class="button">Tweet</a>
                    <a :href="mailtoShareUrl" target="_blank" rel="noopener" class="button">Email</a>
                </div>
            </div>
        </section>

        <section class="feedback">
            <h2>Feedback</h2>
            <p>If you have any questions, suggestions, or comments about this tool, please email <a href="mailto:feedback@shrimpscience.com">feedback@shrimpscience.com</a>.</p>
        </section>
    </div>
</template>

<script>
import { getOverlappingRange, getOverlappingRangeFromArray } from '../lib/compatibility'

export default {
    name: 'SummaryPage',
    mounted() {
        // If no shrimp IDs were provided or found in the store, bounce to landing page
        if(this.selectedShrimp.length === 0) {
            this.goToLandingPage()
        }
    },
    computed: {
        selectedShrimp() {
            return this.$store.getters['selectedShrimp']
        },
        suggestedRanges() {
            return {
                gh: getOverlappingRangeFromArray(this.selectedShrimp.map(shrimp => shrimp.ranges.gh)),
                kh: getOverlappingRangeFromArray(this.selectedShrimp.map(shrimp => shrimp.ranges.kh)),
                ph: getOverlappingRangeFromArray(this.selectedShrimp.map(shrimp => shrimp.ranges.ph)),
            }
        },
        totalShrimpWithWarningsCount() {
            let total = 0

            this.selectedShrimp.forEach(shrimp => {
                let shrimpWarnings = this.getWarningsForShrimp(shrimp)

                if(shrimpWarnings.length > 0) {
                    total++
                }
            })
            return total
        },
        usingSuggestedParameters() {
            return !this.$store.getters['hasInputParameters']
        },
        tankParameters() {
            if(this.usingSuggestedParameters) {
                return this.suggestedRanges
            }

            return this.$store.getters['inputParameters']
        },
        shareUrl() {
            return process.env.VUE_APP_SHARE_DOMAIN + process.env.BASE_URL + '#' + this.$store.getters['getSummaryUri']
        },
        twitterShareUrl() {
            let urlParams = new URLSearchParams({
                text: 'This is my shrimp tank setup using the Shrimp Suggester',
                url: this.shareUrl,
                hashtags: ['shrimptank'].join(',')
            })
            return 'https://twitter.com/intent/tweet?' + urlParams.toString()
        },
        mailtoShareUrl() {
            let urlParams = new URLSearchParams({
                to: '',
                body: this.shareUrl,
                subject: 'My shrimp tank setup'
            })
            return 'mailto:?' + urlParams.toString()
        },
        showNativeShare() {
            return navigator.share !== undefined
        }
    },
    methods: {
        goToLandingPage() {
            this.$router.push('/')
        },
        getParamTextFromRange(paramRange) {
            // Prevent errors when loading a page without any shrimp data, as all shrimp data
            // is unavailable we will not have suggested parameter data either
            if(paramRange === null) {
                return ''
            }

            let min = paramRange[0]
            let max = paramRange[1]

            return min === max ? min : min + ' - ' + max
        },
        getImagePathForShrimp(shrimp) {
            return this.$store.getters['shrimpImageUri'](shrimp.id)
        },
        imageFailure() {
            let defaultImageUri = this.$store.getters['defaultImageUri']

            if(event.target.src === defaultImageUri) {
                // Prevent getting stuck in a loop if the default image fails to load
                return
            }

            event.target.src = defaultImageUri
        },
        getShrimpParameterStatus(shrimp, paramKey) {
            let shrimpParamRange = null
            let tankParamRange = null

            switch(paramKey) {
                case 'gh':
                    shrimpParamRange = shrimp.ranges.gh
                    tankParamRange = this.tankParameters.gh
                    break
                case 'kh':
                    shrimpParamRange = shrimp.ranges.kh
                    tankParamRange = this.tankParameters.kh
                    break
                case 'ph':
                    shrimpParamRange = shrimp.ranges.ph
                    tankParamRange = this.tankParameters.ph
                    break
                default:
                    return ''
            }

            let rangeOverlap = getOverlappingRange(tankParamRange, shrimpParamRange)

            // If there is no valid range overlap, identify the reason why
            if(rangeOverlap === null) {
                if(tankParamRange[0] < shrimpParamRange[0]) {
                    return 'higher'
                }

                if(tankParamRange[1] > shrimpParamRange[1]) {
                    return 'lower'
                }
            }

            return 'ok'
        },
        getWarningsForShrimp(shrimp) {
            let ghStatus = this.getShrimpParameterStatus(shrimp, 'gh')
            let khStatus = this.getShrimpParameterStatus(shrimp, 'kh')
            let phStatus = this.getShrimpParameterStatus(shrimp, 'ph')

            let warnings = []

            if(ghStatus === 'lower') {
                warnings.push('Prefers lower GH')
            } else if (ghStatus === 'higher') {
                warnings.push('Prefers higher GH')
            }

            if(khStatus === 'lower') {
                warnings.push('Prefers lower KH')
            } else if (khStatus === 'higher') {
                warnings.push('Prefers higher KH')
            }

            if(phStatus === 'lower') {
                warnings.push('Prefers lower pH')
            } else if (phStatus === 'higher') {
                warnings.push('Prefers higher pH')
            }

            return warnings
        },
        openSharePrompt() {
            window.navigator.share({
                title: 'My Shrimp Tank',
                text: 'This is my shrimp tank setup using the Shrimp Suggester',
                url: this.shareUrl,
            })
        },
    },
    watch: {
        // Load any URL parameters into the store's state
        '$route.params.tankParams': {
            immediate: true,
            handler(newVal) {
                if(newVal) {
                    if(newVal === 'auto') {
                        this.$store.commit('RESET_INPUT_TANK_PARAMETERS')
                        return
                    }

                    let parsedParams = newVal.split(',')
                    this.$store.commit('SET_INPUT_TANK_PARAMETERS', {
                        gh: parsedParams[0],
                        kh: parsedParams[1],
                        ph: parsedParams[2],
                    })
                }
            }
        },
        '$route.params.shrimpIds': {
            immediate: true,
            handler(newVal) {
                if(newVal) {
                    this.$store.commit('SET_SELECTED_SHRIMP_IDS', newVal.split(','))

                    if(this.$store.state.selectedShrimpIds.length === 0) {
                        this.goToLandingPage()
                    }
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/size";
@import "@/assets/scss/mixins";

$warning-background-color: #fbf0d3;
$warning-border-color: #d8bf7c;
$warning-text-color: #534a2f;

section {
    margin-bottom: 3em;
    background: #ecf5ed;
    padding: 1em;

    h2 {
        margin-top: 0;
        margin-bottom: 0.25em;
    }
}

.tank-parameters-container {
    display: flex;
    justify-content: space-evenly;
    padding: 1em;

    > div {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: 1.2em;

        .label {
            font-weight: bold;
            color: #5a6d5c;
            margin-bottom: 0.5em;
        }
    }
}

.heading-container {
    display: flex;
    align-items: center;
    margin-bottom: 1em;

    .button-container {
        display: flex;
        flex: 1;
        justify-content: flex-end;

        .button {
            justify-self: flex-end;
        }
    }
}

.warning-intro {
    background: $warning-background-color;
    border: 1px solid $warning-border-color;
    color: $warning-text-color;
    padding: 1em;

    p {
        margin: 0;
    }
}

.shrimp-summary-list {
    padding-left: 0;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;

    @include breakpoint(tablet) {
        flex-direction: row;
        justify-content: space-between;
    }

    > li {
        margin-bottom: 1em;
        background-color: white;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(0, 0, 0, 0.2);
        display: flex;
        flex-direction: column;

        @include breakpoint(tablet) {
            width: 49%;
        }

        .shrimp-info {
            display: flex;
            flex-direction: column;
            flex: 1;
            padding: 1em;

            .name-and-image {
                display: flex;
                flex: 1;
                margin-bottom: 1em;

                .image-container {
                    margin-left: 0.5em;
                    margin-right: 1em;
                    flex: 5;
                }

                img {
                    max-width: 100%;
                    object-fit: contain;
                    height: 7em;
                }

                .names {
                    flex: 6;

                    .name {
                        display: block;
                        font-weight: bold;
                        font-size: 1.1em;
                        margin-bottom: 0.25em;
                    }

                    .species {
                        display: block;
                        color: #5f7b62;
                        font-style: italic;
                    }
                }
            }

            .parameters {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                height: 100%;

                & [data-status="higher"],
                & [data-status="lower"] {
                    border: 3px solid $warning-background-color;
                }

                > div {
                    display: flex;
                    align-items: center;
                    flex-direction: column;
                    padding: 0.5em;
                    margin: 0 0.5em;
                    flex: 1;

                    .label {
                        margin-bottom: 0.5em;
                        color: #5a6d5c;
                    }
                }
            }
        }

        .warnings {
            background: $warning-background-color;
            border: 1px solid $warning-border-color;
            color: $warning-text-color;
            padding: 1em;

            ul {
                padding-left: 0;

                li {
                    list-style-type: square;
                    margin: 0.25em 1em;
                }
            }

            .subheading {
                font-weight: bold;
                display: block;
                margin-bottom: 0.5em;
            }
        }
    }
}

section.share {
    .share-link {
        display: block;
        margin-bottom: 1.5em;
    }

    .button-container {
        display: flex;
        align-items: center;

        .button {
            margin-right: 1em;

            &:last-child {
                margin-right: 0;
            }
        }
    }
}
</style>